import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowLeft, ShieldCheck, ArrowRight, Rss } from "lucide-react";
import { track } from "../analytics";
import Seo, {
  SITE_URL,
  breadcrumbLd,
  faqLd,
  organizationLd,
  websiteLd,
} from "../components/Seo";
import { buildMatcher, linkifySections, useGlossaryTerms } from "../components/Linkify";
import { AdUnit } from "../components/AdSense";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLOG_TITLE = "Ingredient Checker Blog: Is This Ingredient Safe? | InZoRAH";
const BLOG_DESC =
  "One ingredient question answered every day. Plain-English explainers on what is in food, drinks, supplements and skincare from the team behind the InZoRAH food ingredient scanner — sourced from USDA, openFDA and Open Food Facts.";

const BlogHeader = () => (
  <header className="blog-top">
    <Link to="/" className="blog-brand">
      InZoRAH
    </Link>
    <nav aria-label="Blog navigation">
      <Link
        to="/ingredients"
        onClick={() => track("click_ingredients_az", { label: "blog header" })}
      >
        Ingredients A-Z
      </Link>
      <Link
        to="/ingredient"
        onClick={() => track("click_ingredient_blog", { label: "blog header" })}
      >
        Ingredient blog
      </Link>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/rss.xml`}>
        <Rss size={13} aria-hidden="true" /> RSS
      </a>
    </nav>
  </header>
);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    return "";
  }
};

/* ---------------------------------------------------- index ---------- */
export const BlogIndex = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/blog/posts`, { params: { limit: 24 } })
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const listLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "The InZoRAH ingredient blog",
    url: `${SITE_URL}/ingredient`,
    description: BLOG_DESC,
    inLanguage: "en",
    publisher: { "@type": "Organization", name: "KYSolutions LLC" },
    blogPost: items.slice(0, 10).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE_URL}/ingredient/${p.slug}`,
      datePublished: p.published_at,
      description: p.excerpt,
    })),
  };

  return (
    <>
      <Seo
        title={BLOG_TITLE}
        description={BLOG_DESC}
        path="/ingredient"
        keywords="ingredient checker, food ingredient scanner, ingredient analyzer, is this ingredient safe, what ingredients, ingredient list, food labels, skincare ingredients"
        jsonLd={[
          listLd,
          organizationLd,
          websiteLd,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Ingredient blog", path: "/ingredient" },
          ]),
        ]}
      />
      <div className="blog-shell">
        <BlogHeader />
        <main className="blog-main">
          <p className="blog-eyebrow">The ingredient blog</p>
          <h1 className="blog-h1">
            What ingredients are really in it — one answer a day.
          </h1>
          <p className="blog-intro">
            Every day we take one ingredient question people actually ask — usually some
            version of <em>is this ingredient safe?</em> — and answer it in plain English:
            what the ingredient is, what it does, what the public record says, and when it
            matters for you. Sources are named and dated. Educational information, not
            medical advice.
          </p>

          {loading && <p className="blog-state">Loading posts…</p>}
          {error && (
            <p className="blog-state">
              Could not load posts right now. Please refresh in a moment.
            </p>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="blog-state">
              The first post is being written right now — check back shortly.
            </p>
          )}

          <div className="blog-grid">
            {items.map((p, i) => (
              <React.Fragment key={p.slug}>
              {i === 4 && <AdUnit variant="display" />}
              <article className="blog-card">
                <Link to={`/ingredient/${p.slug}`} className="blog-card-img">
                  <img
                    src={p.hero_image}
                    srcSet={p.hero_srcset}
                    sizes="(max-width: 720px) 100vw, 380px"
                    alt={p.image_alt || p.title}
                    loading={i === 0 ? "eager" : "lazy"}
                    fetchPriority={i === 0 ? "high" : undefined}
                    decoding="async"
                    width={p.hero_width || 1200}
                    height={p.hero_height || 675}
                  />
                </Link>
                <div className="blog-card-body">
                  <div className="blog-meta">
                    <time dateTime={p.published_at}>{fmtDate(p.published_at)}</time>
                    <span>
                      <Clock size={11} aria-hidden="true" /> {p.read_time} min
                    </span>
                  </div>
                  <h2>
                    <Link to={`/ingredient/${p.slug}`}>{p.title}</Link>
                  </h2>
                  <p>{p.excerpt}</p>
                  <Link className="blog-read" to={`/ingredient/${p.slug}`}>
                    Read the answer <ArrowRight size={14} aria-hidden="true" />
                  </Link>
                </div>
              </article>
              </React.Fragment>
            ))}
          </div>

          {total > 0 && (
            <p className="blog-count">
              {total} ingredient {total === 1 ? "answer" : "answers"} published so far.
            </p>
          )}

          <section className="blog-cta">
            <h2>Want this on your phone instead?</h2>
            <p>
              InZoRAH scans a barcode or label and tells you what ingredients are inside,
              with sources — then finds a cleaner product on the same shelf. Coming to iOS
              and Android.
            </p>
            <Link
              to="/ingredients"
              className="blog-cta-btn"
              onClick={() => track("click_ingredients_az", { label: "blog index cta" })}
            >
              Look up an ingredient
            </Link>
          </section>
        </main>
        <footer className="blog-foot">
          KYSolutions LLC · <Link to="/contact">Contact</Link> ·{" "}
          <a
            className="inz-support-link"
            href="https://royalelite.support"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-portal-link"
          >
            Support
          </a>{" "}
          ·{" "}
          
          <Link to="/legal/privacy">Privacy</Link> ·{" "}
          <Link to="/legal/terms">Terms</Link> · <Link to="/">Home</Link> ·{" "}
          <Link to="/admin/login" rel="nofollow">
            Admin
          </Link>
        </footer>
      </div>
    </>
  );
};

/* ---------------------------------------------------- post ----------- */
export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");
  const terms = useGlossaryTerms();
  const matcher = useMemo(() => buildMatcher(terms), [terms]);
  const linkedSections = useMemo(
    () => linkifySections(post?.sections, matcher, 8),
    [post, matcher]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    axios
      .get(`${API}/blog/posts/${slug}`)
      .then((r) => {
        setPost(r.data?.post || null);
        setRelated(r.data?.related || []);
        setStatus("ok");
        track("read_blog_post", { label: r.data?.post?.title || slug });
      })
      .catch(() => setStatus("missing"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="blog-shell">
        <BlogHeader />
        <main className="blog-main">
          <p className="blog-state">Loading…</p>
        </main>
      </div>
    );
  }

  if (status === "missing" || !post) {
    return (
      <div className="blog-shell">
        <Seo
          title="Post not found | InZoRAH ingredient blog"
          description="This ingredient article could not be found."
          path={`/ingredient/${slug}`}
          noindex
        />
        <BlogHeader />
        <main className="blog-main">
          <h1 className="blog-h1">Post not found</h1>
          <p className="blog-intro">
            That article does not exist (or was renamed).{" "}
            <Link to="/ingredient">Browse all ingredient answers</Link>.
          </p>
        </main>
      </div>
    );
  }

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description,
    image: {
      "@type": "ImageObject",
      url: post.hero_image,
      width: post.hero_width || 1200,
      height: post.hero_height || 675,
      caption: post.image_alt || post.title,
    },
    datePublished: post.published_at,
    dateModified: post.published_at,
    inLanguage: "en",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/ingredient/${post.slug}`,
    },
    author: { "@type": "Organization", name: "InZoRAH" },
    publisher: {
      "@type": "Organization",
      name: "KYSolutions LLC",
    },
    keywords: (post.tags || []).join(", "),
    wordCount: post.word_count,
    articleSection: "Ingredients",
    about: { "@type": "Thing", name: "Food and cosmetic ingredients" },
  };

  return (
    <>
      <Seo
        title={`${post.meta_title || post.title} | InZoRAH`}
        description={post.meta_description}
        path={`/ingredient/${post.slug}`}
        image={post.hero_image}
        type="article"
        publishedTime={post.published_at}
        keywords={["ingredient checker", "is this ingredient safe", ...(post.tags || [])].join(", ")}
        jsonLd={[
          articleLd,
          post.faq && post.faq.length ? faqLd(post.faq) : null,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Ingredient blog", path: "/ingredient" },
            { name: post.title, path: `/ingredient/${post.slug}` },
          ]),
        ].filter(Boolean)}
      />
      <div className="blog-shell">
        <BlogHeader />
        <main className="blog-main blog-article">
          <nav className="blog-crumbs" aria-label="Breadcrumb">
            <Link to="/ingredient">
              <ArrowLeft size={13} aria-hidden="true" /> All ingredient answers
            </Link>
          </nav>

          <h1 className="post-h1">{post.title}</h1>
          <div className="post-meta">
            <time dateTime={post.published_at}>{fmtDate(post.published_at)}</time>
            <span>
              <Clock size={12} aria-hidden="true" /> {post.read_time} min read
            </span>
            {(post.tags || []).map((t) => (
              <em key={t}>{t}</em>
            ))}
          </div>

          <figure className="post-hero">
            <img
              src={post.hero_image}
              srcSet={post.hero_srcset}
              sizes="(max-width: 780px) 100vw, 760px"
              alt={post.image_alt || post.title}
              width={post.hero_width || 1200}
              height={post.hero_height || 675}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <figcaption>{post.image_alt}</figcaption>
          </figure>

          <div className="post-answer">
            <h2>The short answer</h2>
            <p>{post.answer}</p>
          </div>

          {(post.key_takeaways || []).length > 0 && (
            <aside className="post-takeaways" aria-label="Key takeaways">
              <h2>Key takeaways</h2>
              <ul>
                {post.key_takeaways.map((k) => (
                  <li key={k}>{k}</li>
                ))}
              </ul>
            </aside>
          )}

          <div className="post-body">
            {linkedSections.map((s, i) => (
              <React.Fragment key={`${s.heading}-${i}`}>
                <section>
                  <h2>{s.heading}</h2>
                  {s.paragraphNodes.map((nodes, j) => (
                    <p key={j}>{nodes}</p>
                  ))}
                  {s.bulletNodes.length > 0 && (
                    <ul>
                      {s.bulletNodes.map((nodes, j) => (
                        <li key={j}>{nodes}</li>
                      ))}
                    </ul>
                  )}
                </section>
                {/* an in-article unit every third section, from the second on */}
                {i === 0 && <AdUnit variant="in-feed" />}
                {i % 3 === 1 && <AdUnit variant="in-article" />}
              </React.Fragment>
            ))}

            {post.table && post.table.columns && (
              <figure className="post-table">
                <table>
                  <caption>{post.table.caption}</caption>
                  <thead>
                    <tr>
                      {post.table.columns.map((c) => (
                        <th key={c} scope="col">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(post.table.rows || []).map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </figure>
            )}
          </div>

          {(post.faq || []).length > 0 && (
            <section className="post-faq">
              <h2>Questions people also ask</h2>
              {post.faq.map((f) => (
                <div key={f.question}>
                  <h3>{f.question}</h3>
                  <p>{f.answer}</p>
                </div>
              ))}
            </section>
          )}

          {(post.sources || []).length > 0 && (
            <section className="post-sources">
              <h2>Where this came from</h2>
              <ul>
                {post.sources.map((s) => (
                  <li key={s.name}>
                    <ShieldCheck size={13} aria-hidden="true" />
                    <span>
                      <b>{s.name}</b>
                      {s.note ? ` — ${s.note}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="post-disclaimer">
                Educational information, not medical advice. Ingredient records change —
                always read the physical label if you have an allergy.
              </p>
            </section>
          )}

          <section className="blog-cta">
            <h2>Scan it instead of reading it</h2>
            <p>
              InZoRAH reads any ingredient list for you and shows what ingredients are
              inside, with sources, then suggests a cleaner swap. Coming to iOS and Android.
            </p>
            <Link
              to="/ingredients"
              className="blog-cta-btn"
              onClick={() => track("click_ingredients_az", { label: "post cta" })}
            >
              Browse ingredients A-Z
            </Link>
          </section>

          {related.length > 0 && (
            <section className="post-related">
              <h2>More ingredient answers</h2>
              <div className="blog-grid">
                {related.map((r) => (
                  <article className="blog-card" key={r.slug}>
                    <Link to={`/ingredient/${r.slug}`} className="blog-card-img">
                      <img
                        src={r.hero_image}
                        srcSet={r.hero_srcset}
                        sizes="(max-width: 720px) 100vw, 340px"
                        alt={r.image_alt || r.title}
                        loading="lazy"
                        decoding="async"
                        width={r.hero_width || 1200}
                        height={r.hero_height || 675}
                      />
                    </Link>
                    <div className="blog-card-body">
                      <h3>
                        <Link to={`/ingredient/${r.slug}`}>{r.title}</Link>
                      </h3>
                      <p>{r.excerpt}</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </main>
        <footer className="blog-foot">
          KYSolutions LLC · <Link to="/contact">Contact</Link> ·{" "}
          <a
            className="inz-support-link"
            href="https://royalelite.support"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-portal-link"
          >
            Support
          </a>{" "}
          ·{" "}
          
          <Link to="/legal/privacy">Privacy</Link> ·{" "}
          <Link to="/legal/terms">Terms</Link> · <Link to="/">Home</Link> ·{" "}
          <Link to="/admin/login" rel="nofollow">
            Admin
          </Link>
        </footer>
      </div>
    </>
  );
};
